/**
 * SyncManager - Gerenciador de Sincronização
 * Processa a fila de sincronização quando a conexão é restabelecida
 */

import api from '../api';
import {
  SurveyRepository,
  EnvironmentRepository,
  ItemRepository,
  FileRepository,
  SketchRepository,
  SyncQueueManager,
} from './index.js';

const ENTITY_TYPES = SyncQueueManager.ENTITY_TYPES;
const ACTIONS = SyncQueueManager.ACTIONS;

class SyncManager {
  constructor() {
    this.isSyncing = false;
    this.listeners = [];
    this.syncInterval = null;
  }

  /**
   * Inicia o listener de conexão
   */
  start() {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    if (navigator.onLine) {
      this.handleOnline();
    }
  }

  /**
   * Para o listener de conexão
   */
  stop() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.stopAutoSync();
  }

  /**
   * Inicia sync automático a cada X segundos
   */
  startAutoSync(intervalMs = 30000) {
    this.stopAutoSync();
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && !this.isSyncing) {
        this.sync();
      }
    }, intervalMs);
  }

  /**
   * Para sync automático
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  handleOnline = async () => {
    console.log('[SyncManager] Conexão restaurada, iniciando sincronização...');
    this.notifyListeners('online');
    await this.sync();
  };

  handleOffline = () => {
    console.log('[SyncManager] Conexão perdida');
    this.notifyListeners('offline');
  };

  /**
   * Adiciona listener para eventos de sync
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notifyListeners(event, data) {
    this.listeners.forEach(l => l(event, data));
  }

  /**
   * Sincroniza todos os itens pendentes
   */
  async sync() {
    if (this.isSyncing) {
      console.log('[SyncManager] Sincronização já em andamento');
      return { success: false, message: 'Já sincronizando' };
    }

    if (!navigator.onLine) {
      console.log('[SyncManager] Offline, abortando sincronização');
      return { success: false, message: 'Sem conexão' };
    }

    this.isSyncing = true;
    this.notifyListeners('sync_start');

    try {
      const pending = await SyncQueueManager.getPending();
      console.log(`[SyncManager] Processando ${pending.length} itens pendentes`);

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const item of pending) {
        try {
          await this.processQueueItem(item);
          await SyncQueueManager.dequeue(item.id);
          successCount++;
        } catch (error) {
          console.error(`[SyncManager] Erro ao processar item ${item.queueId}:`, error);
          await SyncQueueManager.markFailed(item.id, error.message);
          errorCount++;
          errors.push({ item: item.queueId, error: error.message });
        }
      }

      const result = { successCount, errorCount, errors };
      console.log(`[SyncManager] Sincronização concluída: ${successCount} sucessos, ${errorCount} erros`);
      this.notifyListeners('sync_complete', result);
      return result;
    } catch (error) {
      console.error('[SyncManager] Erro geral na sincronização:', error);
      this.notifyListeners('sync_error', error);
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Processa um item da fila de sincronização
   */
  async processQueueItem(item) {
    const { entity, entityId, action, data } = item;

    console.log(`[SyncManager] Processando ${action} para ${entity}:`, data);

    switch (entity) {
      case ENTITY_TYPES.SURVEY:
        return this.syncSurvey(entityId, action, data);
      case ENTITY_TYPES.ENVIRONMENT:
        return this.syncEnvironment(entityId, action, data);
      case ENTITY_TYPES.ITEM:
        return this.syncItem(entityId, action, data);
      case ENTITY_TYPES.FILE:
        return this.syncFile(entityId, action, data);
      case ENTITY_TYPES.SKETCH_PROJECT:
        return this.syncSketchProject(entityId, action, data);
      case ENTITY_TYPES.SKETCH_ELEMENTS:
        return this.syncSketchElements(entityId, action, data);
      default:
        throw new Error(`Tipo de entidade desconhecido: ${entity}`);
    }
  }

  /**
   * Sincroniza levantamento
   */
  async syncSurvey(entityId, action, data) {
    switch (action) {
      case ACTIONS.CREATE:
        const createResponse = await api.post('/surveys', data);
        const createdSurvey = createResponse.data;
        if (createdSurvey?.id) {
          await SurveyRepository.updateServerId(data.localId, createdSurvey.id);
        }
        return createdSurvey;

      case ACTIONS.UPDATE:
        const localSurvey = await SurveyRepository.getById(entityId);
        if (localSurvey?.serverId) {
          await api.put(`/surveys/${localSurvey.serverId}`, data);
          await SurveyRepository.updateSyncStatus(entityId, 'synced');
        }
        return { success: true };

      case ACTIONS.DELETE:
        const surveyToDelete = await SurveyRepository.getById(entityId);
        if (surveyToDelete?.serverId) {
          await api.delete(`/surveys/${surveyToDelete.serverId}`);
        }
        return { success: true };

      default:
        throw new Error(`Ação desconhecida para survey: ${action}`);
    }
  }

  /**
   * Sincroniza ambiente
   */
  async syncEnvironment(entityId, action, data) {
    const localEnv = await EnvironmentRepository.getById(entityId);
    if (!localEnv) throw new Error('Ambiente não encontrado localmente');

    switch (action) {
      case ACTIONS.CREATE:
        const parentSurvey = await SurveyRepository.getByLocalId(localEnv.localSurveyId);
        if (!parentSurvey?.serverId) throw new Error('Levantamento pai não sincronizado');

        const envCreateResponse = await api.post(`/surveys/${parentSurvey.serverId}/environments`, data);
        const createdEnv = envCreateResponse.data;
        if (createdEnv?.id) {
          await EnvironmentRepository.updateServerId(localEnv.localId, createdEnv.id);
        }
        return createdEnv;

      case ACTIONS.UPDATE:
        if (!localEnv.serverId) throw new Error('Ambiente não tem serverId');
        await api.put(`/surveys/${localEnv.surveyId}/environments/${localEnv.serverId}`, data);
        await EnvironmentRepository.updateSyncStatus(entityId, 'synced');
        return { success: true };

      case ACTIONS.DELETE:
        if (localEnv.serverId) {
          const survey = await SurveyRepository.getByServerId(localEnv.surveyId);
          if (survey?.serverId) {
            await api.delete(`/surveys/${survey.serverId}/environments/${localEnv.serverId}`);
          }
        }
        return { success: true };

      default:
        throw new Error(`Ação desconhecida para environment: ${action}`);
    }
  }

  /**
   * Sincroniza item
   */
  async syncItem(entityId, action, data) {
    const localItem = await ItemRepository.getById(entityId);
    if (!localItem) throw new Error('Item não encontrado localmente');

    switch (action) {
      case ACTIONS.CREATE:
        const parentEnv = await EnvironmentRepository.getByLocalId(localItem.localEnvironmentId);
        if (!parentEnv?.serverId) throw new Error('Ambiente pai não sincronizado');

        const survey = await SurveyRepository.getByLocalId(localItem.localSurveyId);
        if (!survey?.serverId) throw new Error('Levantamento pai não sincronizado');

        const itemCreateResponse = await api.post(
          `/surveys/${survey.serverId}/environments/${parentEnv.serverId}/items`,
          data
        );
        const createdItem = itemCreateResponse.data;
        if (createdItem?.id) {
          await ItemRepository.updateServerId(localItem.localId, createdItem.id);
        }
        return createdItem;

      case ACTIONS.UPDATE:
        if (!localItem.serverId) throw new Error('Item não tem serverId');
        const env = await EnvironmentRepository.getByServerId(localItem.environmentId);
        if (!env) throw new Error('Ambiente não encontrado');
        const parentSurvey = await SurveyRepository.getByServerId(localItem.surveyId);
        if (!parentSurvey) throw new Error('Levantamento não encontrado');

        await api.put(
          `/surveys/${parentSurvey.serverId}/environments/${env.serverId}/items/${localItem.serverId}`,
          data
        );
        await ItemRepository.updateSyncStatus(entityId, 'synced');
        return { success: true };

      case ACTIONS.DELETE:
        if (localItem.serverId) {
          const itemEnv = await EnvironmentRepository.getByServerId(localItem.environmentId);
          const itemSurvey = await SurveyRepository.getByServerId(localItem.surveyId);
          if (itemEnv?.serverId && itemSurvey?.serverId) {
            await api.delete(
              `/surveys/${itemSurvey.serverId}/environments/${itemEnv.serverId}/items/${localItem.serverId}`
            );
          }
        }
        return { success: true };

      default:
        throw new Error(`Ação desconhecida para item: ${action}`);
    }
  }

  /**
   * Sincroniza arquivo
   */
  async syncFile(entityId, action, data) {
    const localFile = await FileRepository.getById(entityId);
    if (!localFile) throw new Error('Arquivo não encontrado localmente');

    switch (action) {
      case ACTIONS.CREATE:
        const blob = await FileRepository.getBlob(localFile.localId);
        if (!blob) throw new Error('Blob do arquivo não encontrado');

        const survey = await SurveyRepository.getByLocalId(localFile.localSurveyId);
        if (!survey?.serverId) throw new Error('Levantamento pai não sincronizado');

        const formData = new FormData();
        formData.append('files', blob, localFile.filename);
        if (localFile.file_type) formData.append('file_type', localFile.file_type);
        if (localFile.category) formData.append('category', localFile.category);
        if (localFile.description) formData.append('description', localFile.description);

        const fileResponse = await api.post(`/surveys/${survey.serverId}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const createdFile = fileResponse.data;
        if (createdFile?.id) {
          await FileRepository.updateServerId(localFile.localId, createdFile.id);
        }
        return createdFile;

      case ACTIONS.DELETE:
        if (localFile.serverId) {
          await api.delete(`/surveys/files/${localFile.serverId}`);
        }
        return { success: true };

      default:
        throw new Error(`Ação desconhecida para file: ${action}`);
    }
  }

  /**
   * Sincroniza projeto de croqui (pranchas, páginas, camadas, dados do canvas)
   */
  async syncSketchProject(entityId, action, data) {
    const localProject = await SketchRepository.getProjectById(entityId);
    if (!localProject) return { success: true };

    switch (action) {
      case ACTIONS.CREATE: {
        // Se pertencer a levantamento/ambiente criados offline, resolve os IDs de servidor
        let targetSurveyId = localProject.survey_id;
        let targetEnvId = localProject.environment_id;

        if (targetSurveyId && String(targetSurveyId).startsWith('local_')) {
          const s = await SurveyRepository.getByLocalId(targetSurveyId);
          if (s?.serverId) targetSurveyId = s.serverId;
        }

        if (targetEnvId && String(targetEnvId).startsWith('local_')) {
          const env = await EnvironmentRepository.getByLocalId(targetEnvId);
          if (env?.serverId) targetEnvId = env.serverId;
        }

        const payload = {
          name: localProject.name || 'Croqui 2D',
          description: localProject.description || '',
          survey_id: targetSurveyId,
          environment_id: targetEnvId,
          canvas_data: localProject.canvas_data || {}
        };

        const res = await api.post('/sketch/projects', payload);
        const created = res.data?.data || res.data;
        if (created?.id) {
          await SketchRepository.saveProject({
            ...localProject,
            serverId: created.id,
            syncStatus: 'synced'
          });

          // Atualiza também o ambiente pai para que o card no levantamento exiba o ID real do servidor
          if (targetSurveyId && targetEnvId) {
            try {
              await api.put(`/surveys/${targetSurveyId}/environments/${targetEnvId}`, {
                sketch_project_id: created.id
              });
            } catch (envErr) {
              console.warn('[SyncManager] Erro ao associar sketch_project_id ao ambiente sincronizado:', envErr);
            }
          }

          // Se houver snapshot pendente, envia agora
          if (localProject.snapshotDataUrl) {
            try {
              await api.post(`/sketch/projects/${created.id}/snapshot`, { snapshot: localProject.snapshotDataUrl });
            } catch (snapErr) {
              console.warn('[SyncManager] Erro ao sincronizar snapshot do croqui:', snapErr);
            }
          }
        }
        return created;
      }

      case ACTIONS.UPDATE: {
        const serverId = localProject.serverId || (String(localProject.id).startsWith('local_') ? null : localProject.id);
        if (!serverId) {
          // Se ainda não tem ID de servidor, roda create
          return this.syncSketchProject(entityId, ACTIONS.CREATE, data);
        }

        await api.put(`/sketch/projects/${serverId}`, {
          name: localProject.name,
          description: localProject.description,
          canvas_data: localProject.canvas_data
        });

        if (localProject.snapshotDataUrl && localProject.hasSnapshotPending) {
          try {
            await api.post(`/sketch/projects/${serverId}/snapshot`, { snapshot: localProject.snapshotDataUrl });
          } catch (snapErr) {
            console.warn('[SyncManager] Erro ao atualizar snapshot do croqui:', snapErr);
          }
        }

        await SketchRepository.saveProject({
          ...localProject,
          syncStatus: 'synced',
          hasSnapshotPending: false
        });

        return { success: true };
      }

      case ACTIONS.DELETE: {
        const serverId = localProject.serverId || (String(localProject.id).startsWith('local_') ? null : localProject.id);
        if (serverId) {
          await api.delete(`/sketch/projects/${serverId}`);
        }
        return { success: true };
      }

      default:
        throw new Error(`Ação desconhecida para sketch_project: ${action}`);
    }
  }

  /**
   * Sincroniza elementos de desenho do croqui
   */
  async syncSketchElements(entityId, action, data) {
    const projectId = data.projectId || entityId;
    const localProject = await SketchRepository.getProjectById(projectId);
    const serverId = localProject?.serverId || (String(projectId).startsWith('local_') ? null : projectId);

    if (!serverId) {
      console.warn('[SyncManager] Aguardando criação do projeto de croqui no servidor para salvar elementos');
      throw new Error('Projeto de croqui no servidor ainda não disponível');
    }

    const elements = data.elements || await SketchRepository.getElements(projectId);
    await api.post(`/sketch/projects/${serverId}/elements`, { elements });
    await SketchRepository.saveElements(projectId, elements);

    return { success: true };
  }

  /**
   * Sincroniza apenas um levantamento específico
   */
  async syncSurveyByLocalId(localId) {
    const survey = await SurveyRepository.getByLocalId(localId);
    if (!survey) throw new Error('Levantamento não encontrado');

    const pending = await SyncQueueManager.getPending();
    const surveyPending = pending.filter(
      item => item.entity === ENTITY_TYPES.SURVEY && item.data.localId === localId
    );

    for (const item of surveyPending) {
      try {
        await this.processQueueItem(item);
        await SyncQueueManager.dequeue(item.id);
      } catch (error) {
        await SyncQueueManager.markFailed(item.id, error.message);
        throw error;
      }
    }

    return { success: true };
  }

  /**
   * Obter status atual
   */
  getStatus() {
    return {
      isSyncing: this.isSyncing,
      isOnline: navigator.onLine,
    };
  }
}

const syncManager = new SyncManager();

export default syncManager;
